/**
 * UUFUN theme customizations - behaviour.
 *
 * Loaded once (type="module") from snippets/uufun-head.liquid. Upstream Horizon files stay
 * untouched: everything here attaches to upstream components from the outside. Keep each
 * feature in its own clearly delimited section and note the upstream version it targets.
 * Inventory and rationale: CUSTOMIZATIONS.md.
 */

/* =========================================================================================
   Slideshow: autoplay progress ring on the pagination dots
   (styles in assets/uufun.css.liquid, toggle: settings.uufun_slideshow_progress_ring)
   Origin: commits 83a68bd, a2381d5, ccc34b6, ca96e6f.

   The ring is a conic-gradient that reads --uufun-ring-progress; this code updates that value
   on each animation frame while autoplay runs. Driving it from JS (rather than a CSS animation
   of a registered custom property) keeps it in step with upstream's timer and avoids depending
   on @property support, which older Safari lacks.
   ========================================================================================= */

const DOTS = '.slideshow-controls__dots button';

/**
 * @param {HTMLElement} slideshow - a <slideshow-component autoplay="N"> element
 */
function setupProgressRing(slideshow) {
  if ('uufunRing' in slideshow.dataset) return;
  slideshow.dataset.uufunRing = '';

  const seconds = parseInt(slideshow.getAttribute('autoplay') || '', 10);
  if (!seconds) return;
  const duration = seconds * 1000;

  let startedAt = performance.now();
  /** @type {number | null} time at which progress was frozen (pause button, hidden tab) */
  let frozenAt = null;

  const restart = () => {
    startedAt = performance.now();
    frozenAt = null;
    for (const dot of slideshow.querySelectorAll(DOTS)) {
      if (dot instanceof HTMLElement) dot.style.removeProperty('--uufun-ring-progress');
    }
  };

  /** @param {number} now */
  const tick = (now) => {
    if (!slideshow.isConnected) return; // section was re-rendered or removed: end this loop

    // Upstream stops advancing while paused or in a hidden tab; freeze the ring with it.
    // Hovering does not pause anything here, see disableHoverPause below.
    const frozen = slideshow.hasAttribute('paused') || document.hidden;
    if (frozen) {
      if (frozenAt == null) frozenAt = now;
    } else {
      if (frozenAt != null) {
        startedAt += now - frozenAt;
        frozenAt = null;
      }
      const dot = slideshow.querySelector(`${DOTS}[aria-selected="true"]`);
      if (dot instanceof HTMLElement) {
        const pct = Math.min(100, ((now - startedAt) / duration) * 100);
        dot.style.setProperty('--uufun-ring-progress', `${pct.toFixed(1)}%`);
      }
    }
    requestAnimationFrame(tick);
  };

  // Upstream removes `paused` whenever its timer (re)starts (play/resume): restart the ring too.
  new MutationObserver(() => {
    if (!slideshow.hasAttribute('paused')) restart();
  }).observe(slideshow, { attributes: true, attributeFilter: ['paused'] });

  // A newly selected dot means a new slide interval has begun.
  new MutationObserver(restart).observe(slideshow, { attributes: true, subtree: true, attributeFilter: ['aria-selected'] });

  // When the shopper picks a slide, restart upstream's timer as well so ring and timer stay aligned.
  slideshow.addEventListener('slideshow:select', (event) => {
    const detail = /** @type {any} */ (event).detail;
    if (detail?.userInitiated && !slideshow.hasAttribute('paused') && typeof (/** @type {any} */ (slideshow)).resume === 'function') {
      /** @type {any} */ (slideshow).resume();
    }
  });

  requestAnimationFrame(tick);
}

/* =========================================================================================
   Slideshow: keep autoplaying while the pointer is over it, and fix the play button
   Origin: commit ca96e6f. Upstream pauses on hover in two places (checked on Horizon 3.5.1 and 4.1.5):
   mouseenter/mouseleave listeners bound to its own suspend/resume methods, and a
   `this.matches(':hover')` check inside the autoplay tick. Both are neutralised from outside so
   the pause/play control is the only thing that stops the show. Re-verify after each merge.
   ========================================================================================= */

customElements.whenDefined('slideshow-component').then((Slideshow) => {
  const proto = Slideshow.prototype;

  /* Upstream wires the play button as on:click="/play", which hands the click *event* to
     play(interval) and starts a ~0ms interval. Coerce anything that is not a number back to
     the default autoplay interval. */
  const play = proto.play;
  proto.play = function (interval) {
    return play.call(this, typeof interval === 'number' ? interval : undefined);
  };

  /** @param {Element} slideshow */
  const disableHoverPause = (slideshow) => {
    if (!(slideshow instanceof HTMLElement) || !slideshow.hasAttribute('autoplay') || 'uufunHover' in slideshow.dataset) return;
    slideshow.dataset.uufunHover = '';
    slideshow.removeEventListener('mouseenter', proto.suspend);
    slideshow.removeEventListener('mouseleave', proto.resume);
    // The autoplay tick asks `this.matches(':hover')`; answer "no" for that one selector only.
    const matches = slideshow.matches.bind(slideshow);
    Object.defineProperty(slideshow, 'matches', {
      configurable: true,
      value: (/** @type {string} */ selector) => (selector === ':hover' ? false : matches(selector)),
    });
  };

  // Elements upgraded before this ran, plus any connected later (editor re-renders, page transitions).
  document.querySelectorAll('slideshow-component').forEach(disableHoverPause);
  const connectedCallback = proto.connectedCallback;
  proto.connectedCallback = function () {
    connectedCallback.call(this);
    disableHoverPause(this);
  };
});

/* =========================================================================================
   Bootstrap: apply enhancements now and whenever new sections render
   (theme editor, page transitions, section rendering API).
   ========================================================================================= */

function enhance(root = document) {
  root.querySelectorAll('slideshow-component[autoplay]').forEach((el) => {
    if (el instanceof HTMLElement) setupProgressRing(el);
  });
}

let scheduled = false;
new MutationObserver(() => {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    enhance();
  });
}).observe(document.documentElement, { childList: true, subtree: true });

enhance();
